import type { SkillEvidenceRef } from '../types';
import { defaultMasteryPolicy, emptyCounters, type MasteryEvidenceKind, type MasteryInput, type MasteryPolicy, type MasterySnapshot, type MasteryState, type SkillCounters } from '../types/mastery';

const DAY = 86_400_000;

/** Evidence kinds that show a learner met an idea but never carry a practice score. */
const UNSCORED_KINDS: ReadonlySet<MasteryEvidenceKind> = new Set<MasteryEvidenceKind>(['lesson', 'transfer', 'knowledge-check']);

/** Counters that record hands-on work. Without scored evidence they still mean the skill is being practiced. */
const PRACTICE_COUNTERS: (keyof SkillCounters)[] = ['labsCompleted', 'codeLabsCompleted', 'vmLabsCompleted', 'scenariosCompleted', 'projectsCompleted', 'interviewAttempts', 'assessmentsPassed'];

/** Counters that record a passed lesson or knowledge check: the idea was met, nothing more. */
const INTRODUCED_COUNTERS: (keyof SkillCounters)[] = ['lessonsCompleted', 'knowledgeChecksPassed'];

const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * Compute a skill's mastery state from evidence. Pure and deterministic: the same refs, counters,
 * time and policy always give the same snapshot. Rules, strongest first:
 *
 * - strong-evidence: at least `strongCount` recent good pieces, or good pieces of at least `strongKinds` different kinds
 * - needs-review: was good before, but nothing good is recent or the latest scored attempt fell below `weakScore`
 * - demonstrated: at least one recent good piece
 * - practicing: scored evidence, none of it good yet; or hands-on work counted with no scores
 * - introduced: a lesson or knowledge check was passed, or evidence carried over from another track (transfer counts as introduced only)
 * - learning: a lesson was started (or time was spent) but nothing was passed
 * - not-started: nothing at all
 *
 * "Good" means a score at or above `goodScore`; "recent" means within `recentDays` of `now`.
 */
export function computeMastery(input: MasteryInput): MasterySnapshot {
  const policy: MasteryPolicy = { ...defaultMasteryPolicy, ...(input.policy ?? {}) };
  const counters: SkillCounters = { ...emptyCounters(), ...input.counters };
  const refs = input.refs;
  const kinds: MasteryEvidenceKind[] = [];
  for (const r of refs) if (!kinds.includes(r.kind)) kinds.push(r.kind);
  const lastEvidenceAt = refs.length ? refs.map((r) => r.at).sort().pop() : undefined;
  const scored = refs.filter((r) => r.score !== undefined && !UNSCORED_KINDS.has(r.kind));
  const base = { counters, evidenceCount: refs.length, scoredCount: scored.length, kinds, lastEvidenceAt };
  const snap = (state: MasteryState, mastery: number, nextEvidence: string): MasterySnapshot => ({ state, mastery, ...base, nextEvidence });

  if (scored.length === 0) {
    const practiced = PRACTICE_COUNTERS.some((k) => counters[k] > 0);
    if (practiced) return snap('practicing', 0, nextEvidenceFor('practicing', policy));
    const introduced = refs.length > 0 || INTRODUCED_COUNTERS.some((k) => counters[k] > 0);
    if (introduced) return snap('introduced', 0, nextEvidenceFor('introduced', policy));
    const started = (Object.keys(counters) as (keyof SkillCounters)[]).some((k) => counters[k] > 0);
    if (started) return snap('learning', 0, nextEvidenceFor('learning', policy));
    return snap('not-started', 0, nextEvidenceFor('not-started', policy));
  }

  const score = (r: SkillEvidenceRef) => r.score ?? 0;
  const mastery = Math.max(...scored.map(score));
  const nowMs = new Date(input.now).getTime();
  const recent = scored.filter((r) => nowMs - new Date(r.at).getTime() <= policy.recentDays * DAY);
  const recentGood = recent.filter((r) => score(r) >= policy.goodScore);
  const latest = [...scored].sort((a, b) => a.at.localeCompare(b.at)).pop();
  const everGood = scored.some((r) => score(r) >= policy.goodScore);
  const goodKinds = new Set(recentGood.map((r) => r.kind)).size;

  if (recentGood.length >= policy.strongCount || goodKinds >= policy.strongKinds) return snap('strong-evidence', mastery, nextEvidenceFor('strong-evidence', policy));
  if (everGood && (recentGood.length === 0 || score(latest!) < policy.weakScore)) {
    const why = recentGood.length === 0 ? `Your last good result is more than ${policy.recentDays} days old.` : `Your latest attempt scored below ${pct(policy.weakScore)}.`;
    return snap('needs-review', mastery, `${why} Replay a lab or scenario that uses this skill and score ${pct(policy.goodScore)} or better.`);
  }
  if (recentGood.length >= 1) {
    const more = policy.strongCount - recentGood.length;
    return snap('demonstrated', mastery, `Show it again: ${more} more recent result${more === 1 ? '' : 's'} at ${pct(policy.goodScore)} or better, or a good result in a different kind of work (for example a scenario after a lab), gives strong evidence.`);
  }
  return snap('practicing', mastery, nextEvidenceFor('practicing', policy));
}

function nextEvidenceFor(state: MasteryState, policy: MasteryPolicy): string {
  switch (state) {
    case 'not-started':
      return 'Start with a lesson that introduces this skill.';
    case 'learning':
      return 'Finish the lesson you started and pass its knowledge check.';
    case 'introduced':
      return 'Try a lab or scenario that uses this skill. Hands-on work is what turns reading into practice.';
    case 'practicing':
      return `Score ${pct(policy.goodScore)} or better in a lab, scenario or project that uses this skill to demonstrate it.`;
    case 'demonstrated':
      return `Show it again at ${pct(policy.goodScore)} or better, ideally in a different kind of work, for strong evidence.`;
    case 'strong-evidence':
      return `Keep it current: practice it again within ${policy.recentDays} days, or package it in a project for your portfolio.`;
    case 'needs-review':
      return `Replay a lab or scenario that uses this skill and score ${pct(policy.goodScore)} or better.`;
  }
}

/**
 * Progress rank used to describe a change. `learning` (started, nothing passed) sits below
 * `introduced` (a lesson passed), and `needs-review` sits between practicing and demonstrated:
 * it was shown once, but the evidence is stale or the latest attempt dipped.
 */
const RANK: Record<MasteryState, number> = { 'not-started': 0, learning: 1, introduced: 2, practicing: 3, 'needs-review': 4, demonstrated: 5, 'strong-evidence': 6 };

/** Direction of a state change, for history rows and notifications. A first snapshot compares against not-started. */
export function masteryTransition(prev: MasteryState | undefined, next: MasteryState): 'up' | 'down' | 'same' {
  const a = RANK[prev ?? 'not-started'];
  const b = RANK[next];
  return b > a ? 'up' : b < a ? 'down' : 'same';
}

const LABELS: Record<MasteryState, string> = {
  'not-started': 'Not started',
  introduced: 'Introduced',
  learning: 'Learning',
  practicing: 'Practicing',
  demonstrated: 'Demonstrated',
  'strong-evidence': 'Strong evidence',
  'needs-review': 'Needs review',
};

const DESCRIPTIONS: Record<MasteryState, string> = {
  'not-started': 'Nothing recorded for this skill yet.',
  introduced: 'You passed a lesson or knowledge check on this skill, or carried evidence over from another track. Reading about a skill is not practicing it yet.',
  learning: 'You have started learning this skill but have not passed a lesson or check on it yet.',
  practicing: 'You have worked on this skill hands-on, but no attempt has reached the demonstrated score yet.',
  demonstrated: 'A recent piece of your work showed this skill at a good score. It is a practice indicator, not a qualification.',
  'strong-evidence': 'Several recent pieces of your work, or different kinds of work, showed this skill at a good score. It is a practice indicator, not a qualification.',
  'needs-review': 'You showed this skill before, but that evidence is getting old or your latest attempt dipped. A refresher brings it back.',
};

/** Sentence-case label for a state. */
export const masteryLabel = (state: MasteryState): string => LABELS[state] ?? LABELS['not-started'];

/** Plain-words description of a state, in the "practice indicator, not a qualification" tone. */
export const masteryDescription = (state: MasteryState): string => DESCRIPTIONS[state] ?? DESCRIPTIONS['not-started'];
